/**
 * Table Renderer Module
 *
 * Handles table row HTML generation for the lots list.
 * Orchestrates specialized cell renderers for clean separation of concerns.
 */

import type { Lot, PortalSettings, State } from '../../types';
import { escapeHtml, escapeAttr } from '../formatting';
import { buildPriceReviewControls } from '../rendering.js';
import { collectLotImages, __isArchived } from '../image';

// Import specialized renderers
import {
  buildPriceStatusContent,
  formatLocationWithDistance,
  getSourceDisplay,
  type GetEffectiveCloseTimeFn,
  type HasCustomCloseTimeFn,
} from './helpers';
import { buildBidCell } from './bidCell';
import { buildEstimateCell } from './estimateCell';
import { buildMeatBadge } from './meatBadge';
import { buildWalkAwayCell } from './walkAwayCell';
import { buildFavoriteCell } from './favoriteCell';
import { buildCountdownCell } from './countdownCell';
import styles from './tableCells.module.css';

type SettingsInput = PortalSettings | null | undefined;

/**
 * Build a complete table row for a lot.
 *
 * Main orchestration function that delegates to specialized cell renderers.
 */
export function buildTableRow(
  lot: Lot,
  state: State,
  settings: SettingsInput,
  getEffectiveCloseTime: GetEffectiveCloseTimeFn,
  hasCustomCloseTime: HasCustomCloseTimeFn
): string {
  const l = lot;
  const isDeleted = !!l.deleted;

  // =============================
  // ACTION BUTTONS
  // =============================

  let editIconButton = '';
  if (!(state.mode === 'auth0' || l.remote)) {
    const editTitle = isDeleted ? 'Editing unavailable for deleted lots' : 'Edit lot';
    const editDisabledAttrs = isDeleted ? ' disabled aria-disabled="true"' : '';
    editIconButton =
      '<button class="' +
      styles.lotActionButton +
      '" data-action="edit" data-id="' +
      escapeAttr(l.id) +
      '" title="' +
      escapeAttr(editTitle) +
      '" aria-label="' +
      escapeAttr(editTitle) +
      '"' +
      editDisabledAttrs +
      '><span aria-hidden="true">✏️</span></button>';
  }

  // =============================
  // CELL RENDERING
  // =============================

  const bidHtml = buildBidCell(l, state, isDeleted);
  const estimateCellContent = buildEstimateCell(l, state);
  const meatOnBoneDisplay = buildMeatBadge(l, settings);
  const walkAwayDisplay = buildWalkAwayCell(l, settings);
  const favoriteCellContent = buildFavoriteCell(l, state, isDeleted, editIconButton);

  // =============================
  // IMAGE THUMBNAIL
  // =============================

  const imageList = collectLotImages(l);
  // Filter out archived images
  const validImages = imageList.filter(entry => {
    const canonical = entry && (entry.canonicalUrl || entry.displayUrl);
    const display = entry && (entry.displayUrl || entry.canonicalUrl);
    return !__isArchived(canonical || display);
  });
  const imageCount = validImages.length;
  let imageThumbnail = '';
  if (imageCount > 0) {
    const firstEntry = validImages[0];
    const displaySrc =
      firstEntry && (firstEntry.displayUrl || firstEntry.canonicalUrl)
        ? firstEntry.displayUrl || firstEntry.canonicalUrl
        : '';
    const canonicalSrc =
      firstEntry && (firstEntry.canonicalUrl || firstEntry.displayUrl)
        ? firstEntry.canonicalUrl || firstEntry.displayUrl
        : displaySrc;
    const lotIdAttr = l.id ? ' data-lot-id="' + escapeAttr(l.id) + '"' : '';
    imageThumbnail =
      '<img src="' +
      escapeAttr(displaySrc) +
      '" alt="Lot thumbnail" class="image-thumbnail" data-action="view-images" data-id="' +
      escapeAttr(l.id) +
      '" data-image-url="' +
      escapeAttr(canonicalSrc) +
      '"' +
      lotIdAttr +
      ' />';
  } else {
    imageThumbnail = '<div class="image-thumbnail-placeholder">—</div>';
  }

  // =============================
  // AUCTIONEER DISPLAY
  // =============================

  let auctioneerDisplay = l.auctioneer || '-';
  // Reject HiBid placeholder logo and archived logos
  const isValidLogo =
    l.auctioneerLogoUrl &&
    l.auctioneerLogoUrl !== 'https://cdn.hibid.com/cdn/images/logos/hibid//mainlogo.png' &&
    !__isArchived(l.auctioneerLogoUrl);
  if (isValidLogo) {
    const logoTitle = l.auctioneer || 'Auctioneer';
    const logoContainer =
      '<div class="auctioneer-logo-frame"><img src="' +
      escapeAttr(l.auctioneerLogoUrl) +
      '" alt="' +
      escapeAttr(logoTitle) +
      '" class="auctioneer-logo" /></div>';
    if (l.auctioneerLink) {
      auctioneerDisplay =
        '<a href="' +
        escapeAttr(l.auctioneerLink) +
        '" target="_blank" rel="noopener" title="' +
        escapeAttr(logoTitle) +
        '" class="auctioneer-logo-link">' +
        logoContainer +
        '</a>';
    } else {
      auctioneerDisplay = logoContainer;
    }
  } else if (l.auctioneer) {
    const auctioneerTitle = escapeAttr(l.auctioneer);
    const auctioneerName = escapeHtml(l.auctioneer);
    const fallbackWrapperStart = '<div class="auctioneer-fallback">';
    const fallbackWrapperEnd = '</div>';
    if (l.auctioneerLink) {
      auctioneerDisplay =
        fallbackWrapperStart +
        '<a href="' +
        escapeAttr(l.auctioneerLink) +
        '" target="_blank" rel="noopener" class="lot-title-link auctioneer-name" title="' +
        auctioneerTitle +
        '">' +
        auctioneerName +
        '</a>' +
        fallbackWrapperEnd;
    } else {
      auctioneerDisplay =
        fallbackWrapperStart +
        '<span class="auctioneer-name" title="' +
        auctioneerTitle +
        '">' +
        auctioneerName +
        '</span>' +
        fallbackWrapperEnd;
    }
  } else {
    auctioneerDisplay = '<div class="auctioneer-fallback">-</div>';
  }

  // =============================
  // PRICE STATUS & CONTROLS
  // =============================

  const priceStatusContent = buildPriceStatusContent(l);
  const PRICE_REVIEW_SPINNER_TEMPLATE = '<span class="spinner" aria-hidden="true"></span>';
  const priceReviewControls = buildPriceReviewControls(l, state, PRICE_REVIEW_SPINNER_TEMPLATE);
  const priceStatusHtml = priceReviewControls ? '' : priceStatusContent;

  // =============================
  // TITLE & OTHER FIELDS
  // =============================

  let titleDisplay = l.url
    ? '<a href="' +
      escapeAttr(l.url) +
      '" target="_blank" rel="noopener" class="lot-title-link">' +
      escapeHtml(l.title || '') +
      '</a>'
    : '<div class="text-strong">' + escapeHtml(l.title || '') + '</div>';

  const deletionBadge = isDeleted
    ? '<span class="lot-status-badge lot-status-badge--deleted">Deleted</span>'
    : '';
  if (deletionBadge) {
    titleDisplay += deletionBadge;
  }

  const shippingDisplay = l.shipping
    ? l.shipping.toLowerCase() === 'yes'
      ? '🚚'
      : l.shipping.toLowerCase() === 'no'
        ? '🚫'
        : escapeHtml(l.shipping)
    : '-';

  const homeZip = state.settings?.zipCode || '';
  const locationDisplay = formatLocationWithDistance(l.location, homeZip, state.settings);

  // =============================
  // COUNTDOWN CELL
  // =============================

  const effectiveCloseAt = getEffectiveCloseTime(l);
  const hasCustomTimeValue = hasCustomCloseTime(l.id);
  const countdownDisplay = buildCountdownCell(l, effectiveCloseAt, hasCustomTimeValue);

  // =============================
  // ASSEMBLE ROW
  // =============================

  const rowClassAttr = ' class="lot-row' + (isDeleted ? ' lot-row--deleted' : '') + '"';
  return (
    '<tr' +
    rowClassAttr +
    '>' +
    '<td class="favorite-cell">' +
    favoriteCellContent +
    '</td>' +
    '<td class="image-thumbnail-cell">' +
    imageThumbnail +
    '</td>' +
    '<td class="title-column">' +
    titleDisplay +
    '</td>' +
    '<td class="cell-text-sm">' +
    bidHtml +
    '</td>' +
    '<td class="cell-text-sm">' +
    meatOnBoneDisplay +
    '</td>' +
    '<td class="cell-text-sm walkaway-cell">' +
    walkAwayDisplay +
    '</td>' +
    '<td class="price-status-cell">' +
    priceStatusHtml +
    priceReviewControls +
    '</td>' +
    '<td class="cell-text-sm">' +
    locationDisplay +
    '</td>' +
    '<td class="cell-text-sm cell-center">' +
    shippingDisplay +
    '</td>' +
    '<td class="cell-text-sm">' +
    countdownDisplay +
    '</td>' +
    '<td>' +
    auctioneerDisplay +
    '</td>' +
    '<td class="cell-center">' +
    getSourceDisplay(l.source) +
    '</td>' +
    '</tr>'
  );
}
